/**
 * src/voices/RecipeVoice.js
 *
 * A general-purpose composable voice driven entirely by a JSON "recipe" object.
 *
 * This is the design-time voice for the workbench. When a designer finds
 * parameters they like, the code-export feature generates a minimal constructor
 * call. Production code should commit to WaterFlowVoice / HeartbeatVoice (or a
 * new named subclass) once the recipe is finalised.
 *
 * Signal graph:
 *   osc[0..N] (type, detuned) → mixGain (÷N)
 *                                  → [lfoGain ← LFO × lfoDepth]   (optional)
 *                                      → outputGain  (ADSR envelope)
 *                                          → destination
 *
 * Gain mapping modes (gainMode param):
 *   'sigmoid'    → alertLevel = σ(x; k, τ)          — rises above threshold
 *   'invSigmoid' → alertLevel = 1 − σ(x; k, τ)      — falls below threshold
 *   'linear'     → alertLevel = x  (identity)
 *   'constant'   → alertLevel = 1  (fixed gain)
 *
 * paramInverse: if true, x = 1 − liveValue before mapping
 */

import { applyADSR, releaseADSR } from "../utils/adsr.js";
import { clamp01, lerp, sigmoid, sigmoidInv } from "../utils/math.js";

export class RecipeVoice {
    // ── static contract ───────────────────────────────────────────────────────

    static defaultParams() {
        return {
            oscCount: 2,
            oscType: "sawtooth",
            baseFreq: 80,
            detuneSpread: 30,

            hasLFO: true,
            lfoType: "sine",
            lfoRateMin: 1.0,
            lfoRateMax: 10,
            lfoDepthMin: 0.01,
            lfoDepthMax: 0.55,

            gainMode: "invSigmoid", // 'sigmoid' | 'invSigmoid' | 'linear' | 'constant'
            paramInverse: false,
            gainLow: 0.04,
            gainHigh: 0.42,
            threshold: 0.5,
            sigmoidK: 12,

            adsr: { attack: 0.1, decay: 0.12, sustain: 0.78, release: 0.25 },
        };
    }

    static meta = {
        label: "Custom",
        liveLabel: "Parameter",
        liveDesc: "0 → 1",
        liveDefault: 0.5,
        curveXLabel: "parameter",
        curves(p) {
            const alertFn = (v) => {
                const x = p.paramInverse ? 1 - clamp01(v) : clamp01(v);
                switch (p.gainMode) {
                    case "sigmoid":
                        return sigmoid(x, p.sigmoidK, p.threshold);
                    case "invSigmoid":
                        return sigmoidInv(x, p.sigmoidK, p.threshold);
                    case "linear":
                        return x;
                    case "constant":
                        return 1;
                    default:
                        return x;
                }
            };
            const cs = [
                {
                    key: "gain",
                    label: "Gain",
                    color: "#e2e8f0",
                    fn: (v) => lerp(p.gainLow, p.gainHigh, alertFn(v)),
                },
            ];
            if (p.hasLFO) {
                cs.push({
                    key: "lfoR",
                    label: "LFO Rate (norm)",
                    color: "#c4b5fd",
                    fn: (v) =>
                        lerp(p.lfoRateMin, p.lfoRateMax, alertFn(v)) /
                        Math.max(p.lfoRateMax, 0.001),
                });
                cs.push({
                    key: "lfoD",
                    label: "LFO Depth",
                    color: "#67e8f9",
                    fn: (v) => lerp(p.lfoDepthMin, p.lfoDepthMax, alertFn(v)),
                });
            }
            return cs;
        },
    };

    // ── constructor ───────────────────────────────────────────────────────────

    constructor(ctx, destination, cfg = {}) {
        this.ctx = ctx;
        const def = RecipeVoice.defaultParams();
        this.cfg = {
            ...def,
            ...cfg,
            adsr: { ...def.adsr, ...(cfg.adsr ?? {}) },
        };
        this._buildGraph(destination);
    }

    _buildGraph(destination) {
        const { ctx, cfg } = this;
        const now = ctx.currentTime;

        this.outputGain = ctx.createGain();
        this.outputGain.gain.setValueAtTime(0, now);
        this.outputGain.connect(destination);

        this.lfoGain = ctx.createGain();
        this.lfoGain.gain.setValueAtTime(1.0, now);
        this.lfoGain.connect(this.outputGain);

        const n = Math.max(1, Math.round(cfg.oscCount));
        const step = n > 1 ? cfg.detuneSpread / (n - 1) : 0;

        this.mixGain = ctx.createGain();
        this.mixGain.gain.setValueAtTime(1 / n, now);
        this.mixGain.connect(this.lfoGain);

        this.oscs = Array.from({ length: n }, (_, i) => {
            const osc = ctx.createOscillator();
            osc.type = cfg.oscType;
            osc.frequency.setValueAtTime(
                cfg.baseFreq + (-cfg.detuneSpread / 2 + i * step),
                now,
            );
            osc.connect(this.mixGain);
            osc.start();
            return osc;
        });

        this._lfo = null;
        this._lfoDepth = null;
        if (cfg.hasLFO) {
            this._lfo = ctx.createOscillator();
            this._lfo.type = cfg.lfoType;
            this._lfo.frequency.setValueAtTime(cfg.lfoRateMin, now);

            this._lfoDepth = ctx.createGain();
            this._lfoDepth.gain.setValueAtTime(cfg.lfoDepthMin, now);

            this._lfo.connect(this._lfoDepth);
            this._lfoDepth.connect(this.lfoGain.gain);
            this._lfo.start();
        }
    }

    // ── internal DSP ─────────────────────────────────────────────────────────

    _alertLevel(v) {
        const x = this.cfg.paramInverse ? 1 - clamp01(v) : clamp01(v);
        switch (this.cfg.gainMode) {
            case "sigmoid":
                return sigmoid(x, this.cfg.sigmoidK, this.cfg.threshold);
            case "invSigmoid":
                return sigmoidInv(x, this.cfg.sigmoidK, this.cfg.threshold);
            case "linear":
                return x;
            case "constant":
                return 1;
            default:
                return x;
        }
    }

    _gain(v) {
        return lerp(this.cfg.gainLow, this.cfg.gainHigh, this._alertLevel(v));
    }
    _lfoR(v) {
        return lerp(
            this.cfg.lfoRateMin,
            this.cfg.lfoRateMax,
            this._alertLevel(v),
        );
    }
    _lfoD(v) {
        return lerp(
            this.cfg.lfoDepthMin,
            this.cfg.lfoDepthMax,
            this._alertLevel(v),
        );
    }

    // ── public interface ──────────────────────────────────────────────────────

    start(liveVal = 0.5) {
        const now = this.ctx.currentTime;
        if (this._lfo) {
            this._lfo.frequency.setValueAtTime(this._lfoR(liveVal), now);
            this._lfoDepth.gain.setValueAtTime(this._lfoD(liveVal), now);
        }
        applyADSR(
            this.outputGain,
            this.ctx,
            this.cfg.adsr,
            this._gain(liveVal),
        );
    }

    update(liveVal) {
        const now = this.ctx.currentTime;
        const sustain = this._gain(liveVal) * this.cfg.adsr.sustain;
        const g = this.outputGain.gain;
        g.cancelScheduledValues(now);
        g.setValueAtTime(g.value, now);
        g.linearRampToValueAtTime(sustain, now + 0.05);
        if (this._lfo) {
            this._lfo.frequency.linearRampToValueAtTime(
                this._lfoR(liveVal),
                now + 0.12,
            );
            this._lfoDepth.gain.linearRampToValueAtTime(
                this._lfoD(liveVal),
                now + 0.12,
            );
        }
    }

    stop(onDone) {
        const silentAt = releaseADSR(this.outputGain, this.ctx, this.cfg.adsr);
        const delay = Math.max(
            0,
            (silentAt - this.ctx.currentTime) * 1000 + 20,
        );
        setTimeout(() => {
            this.dispose();
            onDone?.();
        }, delay);
    }

    dispose() {
        try {
            this.oscs.forEach((o) => o.stop());
        } catch (_) {}
        if (this._lfo)
            try {
                this._lfo.stop();
            } catch (_) {}
        this.oscs.forEach((o) => o.disconnect());
        this.mixGain.disconnect();
        if (this._lfo) {
            this._lfo.disconnect();
            this._lfoDepth.disconnect();
        }
        this.lfoGain.disconnect();
        this.outputGain.disconnect();
    }
}
